import { createSelector } from '@reduxjs/toolkit';
import type { SvpAsset, SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type { UnixTimestamp } from '@vgtv/api-client';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';

import type { Vendor } from '../../types';

import type { WatchtimeState } from './index';

type State = { watchtime: WatchtimeState };

const selectWatchtime = (state: State) => state.watchtime;

export const selectSeriesWatchtime = (
  state: State,
  vendor: Vendor,
  categoryId: SvpCategoryId
) => {
  const assetId = state.watchtime.series[vendor][categoryId];

  return assetId ? selectAssetWatchtimeItem(state, assetId, vendor) : undefined;
};

export const selectAssetIdsForWatchtimeHistory = createSelector(
  (state: State) => state.watchtime.history,
  (history) => history.map((item) => item.assetId)
);

export const selectHasWatchtimeHistory = createSelector(
  selectAssetIdsForWatchtimeHistory,
  (assetIds) => assetIds.length > 0
);

export const selectWatchtimeHistoryMappedByAssetId = createSelector(
  selectWatchtime,
  (watchtimeState) => {
    return watchtimeState.history.reduce<Record<SvpAsset['id'], UnixTimestamp>>(
      (acc, curr) => {
        acc[curr.assetId] = curr.updatedAt;

        return acc;
      },
      {}
    );
  }
);

export function selectAssetWatchtimeItem(
  state: State,
  assetId: SvpAssetId,
  vendor: Vendor
) {
  return state.watchtime.items[vendor]?.[assetId];
}

export const selectWatchtimeUpdatedAt = (state: State) => {
  return state.watchtime.updatedAt;
};

export const selectWatchtimeSavedAt = (state: State) => {
  return state.watchtime.savedAt;
};
